/**
 * Form state transitions.
 *
 * Prefill is a first-class provenance, not a default value. That distinction is the whole
 * point of this module:
 *
 *  - seeding a prefilled answer records the source, the fetch time, and the original value;
 *  - changing a prefilled answer flips provenance to `override` and keeps the original;
 *  - changing it *back* to the original returns it to `prefill` and marks it confirmed,
 *    because the technician has now positively asserted the record was right;
 *  - a prefilled value can also be confirmed without being touched.
 *
 * All four states are distinguishable in the payload. A `defaultValue` gives you none of them.
 */

import { allFields } from './logic.ts';
import type {
  Answer,
  ContextValue,
  FieldEntry,
  FormSchema,
  FormState,
  PrefillRecord,
  PrefillSourceSpec,
  ReferenceValue,
} from './types.ts';

/** Structural equality over the answer union. Order-insensitive for multi-selects. */
export function answersEqual(a: Answer | undefined, b: Answer | undefined): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined || a === null || b === null) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    if (a.every((x) => typeof x === 'string') && b.every((x) => typeof x === 'string')) {
      const left = [...(a as readonly string[])].sort();
      const right = [...(b as readonly string[])].sort();
      return left.every((x, i) => x === right[i]);
    }
    return a.every((x, i) => JSON.stringify(x) === JSON.stringify(b[i]));
  }
  if (typeof a === 'object' && typeof b === 'object') return JSON.stringify(a) === JSON.stringify(b);
  return false;
}

export interface PrefillValue {
  readonly value: Answer;
  readonly spec: PrefillSourceSpec;
  readonly fetchedAt: number;
}

/** Resolved prefill values, keyed by prefill source key. */
export type PrefillBundle = Readonly<Record<string, PrefillValue | undefined>>;

export interface CreateStateOptions {
  readonly schema: FormSchema;
  readonly now: number;
  readonly context?: Readonly<Record<string, ContextValue>>;
  readonly prefill?: PrefillBundle;
}

/**
 * Opens a form.
 *
 * A field bound to a prefill source that resolved to a real value starts as a `prefill` entry.
 * A source marked untrusted is deliberately **not** seeded — its value is surfaced to the
 * technician as context to check against, but it never becomes the answer, because adopting a
 * migration placeholder as an answer is fabricating a reading nobody took.
 */
export function createState(options: CreateStateOptions): FormState {
  const { schema, now, context = {}, prefill = {} } = options;
  const entries: Record<string, FieldEntry> = {};
  const reference: Record<string, ReferenceValue> = {};

  for (const { field } of allFields(schema)) {
    const binding = field.prefill;
    if (!binding) continue;
    const resolved = prefill[binding.source];
    if (!resolved) continue;
    if (resolved.value === null || resolved.value === undefined) continue;
    if (typeof resolved.value === 'string' && resolved.value.trim() === '') continue;

    if (resolved.spec.trusted === false) {
      // Shown beside the question, never adopted as the answer.
      reference[field.id] = {
        value: resolved.value,
        label: resolved.spec.label,
        ...(resolved.spec.mapping
          ? {
              object: resolved.spec.mapping.object,
              field: resolved.spec.mapping.field,
              ...(resolved.spec.mapping.caveat !== undefined ? { caveat: resolved.spec.mapping.caveat } : {}),
            }
          : {}),
      };
      continue;
    }

    const record: PrefillRecord = {
      source: binding.source,
      label: resolved.spec.label,
      ...(resolved.spec.mapping ? { object: resolved.spec.mapping.object, field: resolved.spec.mapping.field } : {}),
      fetchedAt: resolved.fetchedAt,
      originalValue: resolved.value,
      /*
       * `?? true`, NOT `!== false`. The `=== false` case already `continue`d above, so tsc had
       * narrowed this to `true | undefined` and rejected the comparison as dead. `??` substitutes
       * only for `undefined`, so this keeps the original meaning (absent means trusted) AND stays
       * correct if that early guard is ever removed — whereas hardcoding `true` here would
       * silently start marking untrusted sources as trusted the moment the guard moved.
       */
      trusted: resolved.spec.trusted ?? true,
    };

    entries[field.id] = {
      value: resolved.value,
      // Identity and clock values are `auto`: nobody asserted them, the device did.
      provenance: binding.overridable === false ? 'auto' : 'prefill',
      prefill: record,
      updatedAt: now,
    };
  }

  return {
    schemaId: schema.id,
    schemaVersion: schema.version,
    startedAt: now,
    context,
    entries,
    reference,
  };
}

/**
 * Records an answer.
 *
 * The override bookkeeping lives here rather than in the renderer so that every write path —
 * touch input, barcode scan, review-page correction — produces the same provenance.
 */
export function setValue(state: FormState, fieldId: string, value: Answer, now: number): FormState {
  const previous = state.entries[fieldId];
  let next: FieldEntry;

  if (previous?.prefill) {
    const backToOriginal = answersEqual(value, previous.prefill.originalValue);
    next = backToOriginal
      ? {
          value,
          // Provenance returns to `prefill`, and the round-trip counts as an explicit
          // confirmation: the technician looked at it and re-asserted the record's value.
          provenance: previous.provenance === 'auto' ? 'auto' : 'prefill',
          prefill: previous.prefill,
          updatedAt: now,
          confirmedAt: now,
        }
      : {
          value,
          provenance: 'override',
          prefill: previous.prefill,
          updatedAt: now,
          overriddenAt: now,
        };
  } else {
    next = { value, provenance: 'technician', updatedAt: now };
  }

  return { ...state, entries: { ...state.entries, [fieldId]: next } };
}

/**
 * Affirms a prefilled value without changing it.
 *
 * Deleting a prefillable question outright loses the confirmation signal, which several of
 * these forms exist to collect. Confirming keeps it at one tap.
 */
export function confirmPrefill(state: FormState, fieldId: string, now: number): FormState {
  const previous = state.entries[fieldId];
  if (!previous?.prefill) return state;
  return {
    ...state,
    entries: { ...state.entries, [fieldId]: { ...previous, confirmedAt: now } },
  };
}

/** Confirms every currently prefilled field in one action, for the review page's bulk affirm. */
export function confirmAllPrefills(state: FormState, now: number): FormState {
  let next = state;
  for (const [id, entry] of Object.entries(state.entries)) {
    if (entry.prefill && entry.provenance !== 'override') next = confirmPrefill(next, id, now);
  }
  return next;
}

/** Discards an answer entirely. A prefilled field reverts to the record's value, not to blank. */
export function clearValue(state: FormState, fieldId: string, now: number): FormState {
  const previous = state.entries[fieldId];
  if (!previous) return state;
  if (previous.prefill) {
    return {
      ...state,
      entries: {
        ...state.entries,
        [fieldId]: {
          value: previous.prefill.originalValue,
          provenance: previous.provenance === 'auto' ? 'auto' : 'prefill',
          prefill: previous.prefill,
          updatedAt: now,
        },
      },
    };
  }
  const entries = { ...state.entries };
  delete entries[fieldId];
  return { ...state, entries };
}

export function valueOf(state: FormState, fieldId: string): Answer | undefined {
  return state.entries[fieldId]?.value;
}
